import React, { useState, useEffect, useRef } from 'react';
import { Play, Clock, CheckCircle, XCircle, RefreshCw, FileText, ToggleLeft, ToggleRight, Upload, FileSpreadsheet, Download } from 'lucide-react';
import { toast } from 'react-toastify';
import { processarManual, toggleAgendamento, processarExcel, downloadExemploExcel, obterStatusProcessamento } from '../services/api';
import api from '../services/api';
import './Dashboard.css';

const Dashboard = () => {
  const [loading, setLoading] = useState(false);
  const [configuracoes, setConfiguracoes] = useState(null);
  const [estatisticas, setEstatisticas] = useState({
    totalProcessados: 0,
    totalEnviados: 0,
    ultimoProcessamento: null
  });
  const [mostrarManual, setMostrarManual] = useState(false);
  const [dadosJson, setDadosJson] = useState('');
  const [processandoManual, setProcessandoManual] = useState(false);
  const [alternandoAgendamento, setAlternandoAgendamento] = useState(false);
  const [processandoExcel, setProcessandoExcel] = useState(false);
  const [arquivoExcel, setArquivoExcel] = useState(null);
  const [templateSelecionado, setTemplateSelecionado] = useState('1a-parcela'); // '1a-parcela', 'geral', 'chamamento', 'chamamento-anistia', 'cancelamento-acordo'
  const fileInputRef = useRef(null);
  const [mostrarModalProgresso, setMostrarModalProgresso] = useState(false);
  const [statusProcessamento, setStatusProcessamento] = useState(null);
  const [jobIdAtual, setJobIdAtual] = useState(null);
  const intervaloStatusRef = useRef(null);

  useEffect(() => {
    carregarConfiguracoes();
    carregarEstatisticas();
  }, []);

  const carregarConfiguracoes = async () => {
    try {
      const response = await api.get('/configuracoes');
      setConfiguracoes(response.data);
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
    }
  };

  const carregarEstatisticas = async () => {
    try {
      const response = await api.get('/estatisticas');
      const stats = response.data;
      
      setEstatisticas({
        totalProcessados: stats.total_processados || 0,
        totalEnviados: stats.total_enviados || 0,
        ultimoProcessamento: stats.ultimo_processamento
      });
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  const iniciarPollingJob = (jobId) => {
    setJobIdAtual(jobId);
    setStatusProcessamento({
      job_id: jobId,
      status: 'pendente',
      progresso: 0,
      total: 0,
      processados: 0,
      enviados: 0,
      erros: []
    });
    setMostrarModalProgresso(true);
    if (intervaloStatusRef.current) {
      clearInterval(intervaloStatusRef.current);
    }
    intervaloStatusRef.current = setInterval(() => {
      consultarStatusProcessamento(jobId);
    }, 2000);
    consultarStatusProcessamento(jobId);
  };

  const executarProcessamento = async () => {
    setLoading(true);
    try {
      const response = await api.post('/processar-arquivo');
      const resultado = response.data;
      
      if (resultado.job_id) {
        iniciarPollingJob(resultado.job_id);
        toast.info('Processamento FTP enfileirado. Acompanhe o progresso.');
      } else if (resultado.sucesso) {
        toast.success(`Processamento concluído! ${resultado.total_enviados} lembretes enviados.`);
        setEstatisticas(prev => ({
          ...prev,
          totalProcessados: resultado.total_processados,
          totalEnviados: resultado.total_enviados
        }));
      } else {
        toast.error('Erro no processamento. Verifique os logs.');
      }
    } catch (error) {
      toast.error('Erro ao executar processamento: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const processarDadosManuais = async () => {
    if (!dadosJson.trim()) {
      toast.error('Por favor, insira os dados JSON.');
      return;
    }

    setProcessandoManual(true);
    try {
      const response = await processarManual(dadosJson);
      const resultado = response.data;
      
      if (resultado.job_id) {
        iniciarPollingJob(resultado.job_id);
        setDadosJson('');
        setMostrarManual(false);
        toast.info('Processamento manual enfileirado. Acompanhe o progresso.');
      } else {
        toast.success(`Processamento manual concluído! ${resultado.total_enviados} lembretes enviados.`);
        setDadosJson('');
        setMostrarManual(false);
        carregarEstatisticas();
      }
      
    } catch (error) {
      console.error('Erro no processamento manual:', error);
      toast.error('Erro ao processar dados manuais. Verifique o formato JSON.');
    } finally {
      setProcessandoManual(false);
    }
  };

  const alternarAgendamento = async () => {
    setAlternandoAgendamento(true);
    try {
      const response = await toggleAgendamento();
      const resultado = response.data;
      
      toast.success(resultado.message);
      
      // Recarregar configurações
      carregarConfiguracoes();
      
    } catch (error) {
      console.error('Erro ao alternar agendamento:', error);
      toast.error('Erro ao alterar agendamento.');
    } finally {
      setAlternandoAgendamento(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validar se é arquivo Excel
      if (!file.name.toLowerCase().endsWith('.xlsx') && !file.name.toLowerCase().endsWith('.xls')) {
        toast.error('Por favor, selecione um arquivo Excel (.xlsx ou .xls)');
        return;
      }
      setArquivoExcel(file);
    }
  };

  // Função para consultar status do processamento
  const consultarStatusProcessamento = async (jobId) => {
    try {
      const response = await obterStatusProcessamento(jobId);
      const status = response.data;
      setStatusProcessamento(status);
      
      // Se concluído, erro ou órfão, parar polling e atualizar estatísticas
      if (status.status === 'concluido' || status.status === 'erro' || status.status === 'orphaned') {
        if (intervaloStatusRef.current) {
          clearInterval(intervaloStatusRef.current);
          intervaloStatusRef.current = null;
        }
        
        // Recarregar estatísticas
        carregarEstatisticas();
        
        // Mostrar notificação final
        if (status.status === 'concluido') {
          toast.success(`Processamento concluído! ${status.enviados} lembretes enviados de ${status.total} processados.`);
        } else if (status.status === 'orphaned') {
          toast.error('Processamento interrompido (job órfão). Verifique o worker e não reenvie automaticamente.');
        } else {
          toast.error(`Processamento finalizado com erros. ${status.enviados} enviados de ${status.total} processados.`);
        }
      }
    } catch (error) {
      console.error('Erro ao consultar status:', error);
      // Se erro 404, job não existe mais - parar polling
      if (error.response?.status === 404) {
        if (intervaloStatusRef.current) {
          clearInterval(intervaloStatusRef.current);
          intervaloStatusRef.current = null;
        }
        setMostrarModalProgresso(false);
        toast.error('Job de processamento não encontrado.');
      }
    }
  };

  // Limpar intervalo ao desmontar componente
  useEffect(() => {
    return () => {
      if (intervaloStatusRef.current) {
        clearInterval(intervaloStatusRef.current);
      }
    };
  }, []);

  const processarArquivoExcel = async () => {
    if (!arquivoExcel) {
      toast.error('Por favor, selecione um arquivo Excel primeiro.');
      return;
    }

    // Mapear template selecionado para template_id
    const templateIds = {
      '1a-parcela': 'b14cb8b4-f1ee-4f7b-84a6-56a27e87a25e',
      'geral': '86c9caec-f3cb-4ae8-ac9b-9e7590d240a3',
      'chamamento': 'f6657bd9-f53b-4d97-be35-a38aa8b25096',
      'chamamento-anistia': 'de4bad6a-051d-4eb7-8445-251ed04d616f',
      'cancelamento-acordo': '93290069-c892-488f-a7b8-c289cbbfb2c5',
    };
    const templateId = templateIds[templateSelecionado];

    setProcessandoExcel(true);
    
    try {
      const response = await processarExcel(arquivoExcel, templateId);
      const resultado = response.data;
      
      // Verificar se retornou job_id (processamento em background)
      if (resultado.job_id) {
        iniciarPollingJob(resultado.job_id);
        toast.info('Processamento iniciado em background. Você pode continuar usando o sistema.');
      } else {
        // Fallback para resposta antiga (compatibilidade)
        if (resultado.sucesso) {
          toast.success(`Processamento Excel concluído! ${resultado.total_enviados} lembretes enviados de ${resultado.total_processados} processados.`);
          carregarEstatisticas();
        } else {
          toast.error('Erro no processamento Excel. Verifique os logs.');
        }
      }
      
      // Limpar arquivo selecionado
      setArquivoExcel(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
    } catch (error) {
      console.error('Erro no processamento Excel:', error);
      toast.error('Erro ao processar arquivo Excel: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessandoExcel(false);
    }
  };

  const fecharModalProgresso = () => {
    // Não fechar se ainda estiver processando
    if (statusProcessamento && (statusProcessamento.status === 'pendente' || statusProcessamento.status === 'processando')) {
      toast.info('Processamento em andamento. Você pode fechar esta janela e continuar usando o sistema.');
      return;
    }
    
    // Parar polling
    if (intervaloStatusRef.current) {
      clearInterval(intervaloStatusRef.current);
      intervaloStatusRef.current = null;
    }
    
    setMostrarModalProgresso(false);
    setStatusProcessamento(null);
    setJobIdAtual(null);
  };

  const baixarExemploExcel = async () => {
    try {
      const response = await downloadExemploExcel();
      
      // Criar blob e fazer download
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'exemplo_lembretes.xls';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Arquivo de exemplo baixado com sucesso!');
      
    } catch (error) {
      console.error('Erro ao baixar exemplo Excel:', error);
      toast.error('Erro ao baixar arquivo de exemplo.');
    }
  };

  const exemploJson = `[
  {
    "Tipo de Negociação": "PREFIS",
    "Código Negociação": 777329,
    "Data Negociação": "13-03-2024",
    "Responsável": "JOSÉ MARIA CORDEIRO DE OLIVEIRA",
    "Inscrição Municipal": "2.1.300.079.002",
    "Data vencimento da parcela": "12-09-2025",
    "Número da parcela": 18,
    "Qt total de parcelas do acordo": 20,
    "Linha Digitável": "81650000003714414332025092300000000042026543",
    "QrCode Pix": "00020101021226850014br.gov.bcb.pix2563qrcodepix.bb.com.br/pix/v2/9d2aec3c-7f6a-417e-8c7d-15eaba327ea15204000053039865406371.445802BR5925MUNICIPIO DE DUQUE DE CAX6015DUQUE DE CAXIAS62070503***63042C07",
    "Valor da parcela": 360.6200,
    "Celular Responsável": "21972260072",
    "Email Responsável": "1234@gmail.com"
  }
]`;

  const formatarData = (data) => {
    if (!data) return 'Nunca';
    return new Date(data).toLocaleString('pt-BR');
  };

  return (
    <div className="container">
      <div className="dashboard-header">
        <h1 className="dashboard-page-title">Dashboard</h1>
        <p className="dashboard-page-subtitle">
          Sistema de Lembretes WhatsApp - Município de Duque de Caxias
        </p>
      </div>

      <div className="dashboard-stats-grid">
        <div className="dashboard-stat-card">
          <div className="dashboard-stat-icon bg-blue-50">
            <RefreshCw className="text-blue-600" size={24} />
          </div>
          <div className="dashboard-stat-content">
            <h3 className="dashboard-stat-title">Total Processados</h3>
            <p className="dashboard-stat-value">{estatisticas.totalProcessados}</p>
          </div>
        </div>

        <div className="dashboard-stat-card">
          <div className="dashboard-stat-icon bg-green-50">
            <CheckCircle className="text-green-600" size={24} />
          </div>
          <div className="dashboard-stat-content">
            <h3 className="dashboard-stat-title">Enviados com Sucesso</h3>
            <p className="dashboard-stat-value">{estatisticas.totalEnviados}</p>
          </div>
        </div>

        <div className="dashboard-stat-card">
          <div className="dashboard-stat-icon bg-blue-50">
            <Clock className="text-blue-600" size={24} />
          </div>
          <div className="dashboard-stat-content">
            <h3 className="dashboard-stat-title">Última Execução</h3>
            <p className="dashboard-stat-value">{formatarData(estatisticas.ultimoProcessamento)}</p>
          </div>
        </div>
      </div>

      <div className="dashboard-actions-grid">
        <div className="dashboard-card">
          <h2 className="dashboard-card-title">Execução Manual</h2>
          <p className="dashboard-card-description">
            Execute o processamento manualmente para baixar o arquivo do FTP e enviar lembretes.
          </p>
          
          <button 
            className="btn btn-primary"
            onClick={executarProcessamento}
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="loading"></div>
                Processando...
              </>
            ) : (
              <>
                <Play size={18} />
                Executar Agora
              </>
            )}
          </button>
        </div>

        <div className="dashboard-card">
          <h2 className="dashboard-card-title">Status do Sistema</h2>
          
          {configuracoes ? (
            <div className="dashboard-status-list">
              <div className="dashboard-status-item">
                <span className="dashboard-status-label">FTP:</span>
                <span className={`status-badge ${configuracoes.ftp_host ? 'status-success' : 'status-error'}`}>
                  {configuracoes.ftp_host ? 'Configurado' : 'Não configurado'}
                </span>
              </div>
              
              <div className="dashboard-status-item">
                <span className="dashboard-status-label">WhatsApp:</span>
                <span className={`status-badge ${configuracoes.whatsapp_api_url ? 'status-success' : 'status-error'}`}>
                  {configuracoes.whatsapp_api_url ? 'Configurado' : 'Não configurado'}
                </span>
              </div>
              
              <div className="dashboard-status-item">
                <span className="dashboard-status-label">Agendamento:</span>
                <span className={`status-badge ${configuracoes.agendamento_ativo ? 'status-success' : 'status-warning'}`}>
                  {configuracoes.agendamento_ativo ? 'Ativo' : 'Inativo'}
                </span>
              </div>
              
              {configuracoes.proximo_execucao && (
                <div className="dashboard-status-item">
                  <span className="dashboard-status-label">Próxima execução:</span>
                  <span className="dashboard-status-value">{formatarData(configuracoes.proximo_execucao)}</span>
                </div>
              )}
            </div>
          ) : (
            <p>Carregando configurações...</p>
          )}
        </div>

        <div className="dashboard-card">
          <h2 className="dashboard-card-title">Processamento Manual</h2>
          <p className="dashboard-card-description">
            Insira dados JSON manualmente para testar o sistema ou processar registros específicos.
          </p>
          
          <button 
            className="btn btn-secondary"
            onClick={() => setMostrarManual(true)}
          >
            <FileText size={18} />
            Inserir Dados JSON
          </button>
        </div>

        <div className="dashboard-card">
          <h2 className="dashboard-card-title">Processamento Excel</h2>
          <p className="dashboard-card-description">
            Faça upload de um arquivo Excel (.xlsx ou .xls) para processar lembretes automaticamente.
          </p>
          
          <div className="dashboard-excel-actions">
            <button 
              className="dashboard-btn-outline dashboard-btn-download"
              onClick={baixarExemploExcel}
            >
              <Download size={18} />
              Baixar Exemplo Excel
            </button>
          </div>
          
          <div className="dashboard-excel-template-select" style={{ marginBottom: '1rem' }}>
            <label htmlFor="template-select" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
              Template:
            </label>
            <select
              id="template-select"
              value={templateSelecionado}
              onChange={(e) => setTemplateSelecionado(e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                backgroundColor: 'white',
                color: '#374151'
              }}
            >
              <option value="1a-parcela">1a. Parcela</option>
              <option value="geral">Geral</option>
              <option value="chamamento">Chamamento</option>
              <option value="chamamento-anistia">Chamamento Anistia 2026</option>
              <option value="cancelamento-acordo">Cancelamento de Acordo</option>
            </select>
          </div>
          
          <div className="dashboard-excel-upload-container">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            
            <button 
              className="dashboard-btn-outline dashboard-btn-upload"
              onClick={() => fileInputRef.current?.click()}
              disabled={processandoExcel}
            >
              <Upload size={18} />
              Selecionar Arquivo Excel
            </button>
            
            {arquivoExcel && (
              <div className="dashboard-file-selected">
                <FileSpreadsheet size={16} className="text-green-600" />
                <span className="dashboard-file-name">{arquivoExcel.name}</span>
                <span className="dashboard-file-size">({(arquivoExcel.size / 1024).toFixed(1)} KB)</span>
              </div>
            )}
            
            {arquivoExcel && (
              <button 
                className="btn btn-primary dashboard-mt-3"
                onClick={processarArquivoExcel}
                disabled={processandoExcel}
              >
                {processandoExcel ? (
                  <>
                    <div className="loading"></div>
                    Processando...
                  </>
                ) : (
                  <>
                    <Play size={18} />
                    Processar Excel
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        <div className="dashboard-card">
          <h2 className="dashboard-card-title">Controle de Agendamento</h2>
          <p className="dashboard-card-description">
            Ative ou desative o processamento automático diário.
          </p>
          
          <button 
            className={`btn ${configuracoes?.agendamento_ativo ? 'btn-warning' : 'btn-success'}`}
            onClick={alternarAgendamento}
            disabled={alternandoAgendamento}
          >
            {alternandoAgendamento ? (
              <>
                <div className="loading"></div>
                Alternando...
              </>
            ) : (
              <>
                {configuracoes?.agendamento_ativo ? (
                  <>
                    <ToggleLeft size={18} />
                    Desativar Agendamento
                  </>
                ) : (
                  <>
                    <ToggleRight size={18} />
                    Ativar Agendamento
                  </>
                )}
              </>
            )}
          </button>
          
          {configuracoes && !configuracoes.agendamento_ativo && (
            <p className="text-sm text-gray-600 mt-2">
              Para ativar, configure um horário nas Configurações primeiro.
            </p>
          )}
        </div>
      </div>

      {/* Modal para inserção manual de dados */}
      {mostrarManual && (
        <div className="dashboard-modal-overlay">
          <div className="dashboard-modal-content">
            <div className="dashboard-modal-header">
              <h3 className="dashboard-modal-title">Inserir Dados JSON</h3>
              <button 
                className="dashboard-modal-close"
                onClick={() => setMostrarManual(false)}
              >
                <XCircle size={20} />
              </button>
            </div>
            
            <div className="dashboard-modal-body">
              <p className="dashboard-modal-description">
                Cole aqui os dados JSON no formato esperado pelo sistema:
              </p>
              
              <div className="dashboard-json-example">
                <button 
                  className="btn btn-sm dashboard-btn-outline"
                  onClick={() => setDadosJson(exemploJson)}
                >
                  Carregar Exemplo
                </button>
              </div>
              
              <textarea
                className="dashboard-json-textarea"
                value={dadosJson}
                onChange={(e) => setDadosJson(e.target.value)}
                placeholder="Cole aqui os dados JSON..."
                rows={15}
              />
            </div>
            
            <div className="dashboard-modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => setMostrarManual(false)}
              >
                Cancelar
              </button>
              <button 
                className="btn btn-primary"
                onClick={processarDadosManuais}
                disabled={processandoManual || !dadosJson.trim()}
              >
                {processandoManual ? (
                  <>
                    <div className="loading"></div>
                    Processando...
                  </>
                ) : (
                  <>
                    <Play size={18} />
                    Processar Dados
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Progresso do Processamento Excel */}
      {mostrarModalProgresso && statusProcessamento && (
        <div className="dashboard-modal-overlay">
          <div className="dashboard-modal-content" style={{ maxWidth: '600px' }}>
            <div className="dashboard-modal-header">
              <h3 className="dashboard-modal-title">Processamento Excel em Andamento</h3>
              {(statusProcessamento.status === 'concluido' || statusProcessamento.status === 'erro') && (
                <button 
                  className="dashboard-modal-close"
                  onClick={fecharModalProgresso}
                >
                  <XCircle size={20} />
                </button>
              )}
            </div>
            
            <div className="dashboard-modal-body">
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ fontWeight: '500', color: '#374151' }}>Progresso</span>
                  <span style={{ fontWeight: '600', color: '#059669' }}>
                    {statusProcessamento.progresso}%
                  </span>
                </div>
                
                {/* Barra de progresso */}
                <div style={{
                  width: '100%',
                  height: '24px',
                  backgroundColor: '#e5e7eb',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  position: 'relative'
                }}>
                  <div style={{
                    width: `${statusProcessamento.progresso}%`,
                    height: '100%',
                    backgroundColor: statusProcessamento.status === 'erro' ? '#ef4444' : 
                                   statusProcessamento.status === 'concluido' ? '#10b981' : '#3b82f6',
                    transition: 'width 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '0.75rem',
                    fontWeight: '600'
                  }}>
                    {statusProcessamento.progresso > 10 && `${statusProcessamento.progresso}%`}
                  </div>
                </div>
              </div>

              {/* Status e estatísticas */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr', 
                gap: '1rem',
                marginBottom: '1.5rem'
              }}>
                <div style={{ 
                  padding: '1rem', 
                  backgroundColor: '#f9fafb', 
                  borderRadius: '0.5rem',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                    Status
                  </div>
                  <div style={{ 
                    fontSize: '1rem', 
                    fontWeight: '600',
                    color: statusProcessamento.status === 'concluido' ? '#10b981' :
                           statusProcessamento.status === 'erro' ? '#ef4444' :
                           statusProcessamento.status === 'processando' ? '#3b82f6' : '#6b7280'
                  }}>
                    {statusProcessamento.status === 'pendente' && 'Pendente'}
                    {statusProcessamento.status === 'processando' && 'Processando...'}
                    {statusProcessamento.status === 'concluido' && 'Concluído'}
                    {statusProcessamento.status === 'erro' && 'Erro'}
                  </div>
                </div>

                <div style={{ 
                  padding: '1rem', 
                  backgroundColor: '#f9fafb', 
                  borderRadius: '0.5rem',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                    Total
                  </div>
                  <div style={{ fontSize: '1rem', fontWeight: '600', color: '#374151' }}>
                    {statusProcessamento.total || 0}
                  </div>
                </div>

                <div style={{ 
                  padding: '1rem', 
                  backgroundColor: '#f9fafb', 
                  borderRadius: '0.5rem',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                    Processados
                  </div>
                  <div style={{ fontSize: '1rem', fontWeight: '600', color: '#374151' }}>
                    {statusProcessamento.processados || 0}
                  </div>
                </div>

                <div style={{ 
                  padding: '1rem', 
                  backgroundColor: '#f9fafb', 
                  borderRadius: '0.5rem',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                    Enviados
                  </div>
                  <div style={{ fontSize: '1rem', fontWeight: '600', color: '#10b981' }}>
                    {statusProcessamento.enviados || 0}
                  </div>
                </div>
              </div>

              {/* Erros se houver */}
              {statusProcessamento.erros && statusProcessamento.erros.length > 0 && (
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#fef2f2',
                  borderRadius: '0.5rem',
                  border: '1px solid #fecaca',
                  marginTop: '1rem'
                }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#dc2626', marginBottom: '0.5rem' }}>
                    Erros ({statusProcessamento.erros.length})
                  </div>
                  <div style={{ 
                    maxHeight: '150px', 
                    overflowY: 'auto',
                    fontSize: '0.75rem',
                    color: '#991b1b'
                  }}>
                    {statusProcessamento.erros.slice(0, 5).map((erro, index) => (
                      <div key={index} style={{ marginBottom: '0.25rem' }}>
                        • {erro}
                      </div>
                    ))}
                    {statusProcessamento.erros.length > 5 && (
                      <div style={{ fontStyle: 'italic', marginTop: '0.5rem' }}>
                        ... e mais {statusProcessamento.erros.length - 5} erro(s)
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Mensagem informativa */}
              {(statusProcessamento.status === 'pendente' || statusProcessamento.status === 'processando') && (
                <div style={{
                  marginTop: '1rem',
                  padding: '0.75rem',
                  backgroundColor: '#eff6ff',
                  borderRadius: '0.5rem',
                  border: '1px solid #bfdbfe',
                  fontSize: '0.875rem',
                  color: '#1e40af'
                }}>
                  💡 Você pode fechar esta janela e continuar usando o sistema. O processamento continuará em background.
                </div>
              )}
            </div>
            
            <div className="dashboard-modal-footer">
              {(statusProcessamento.status === 'concluido' || statusProcessamento.status === 'erro') ? (
                <button 
                  className="btn btn-primary"
                  onClick={fecharModalProgresso}
                >
                  Fechar
                </button>
              ) : (
                <button 
                  className="btn btn-secondary"
                  onClick={fecharModalProgresso}
                >
                  Fechar (continuar em background)
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {configuracoes && (!configuracoes.ftp_host || !configuracoes.whatsapp_api_url) && (
        <div className="dashboard-card bg-red-50">
          <h3 className="text-red-600 font-semibold mb-4">
            <XCircle size={20} className="inline mr-2" />
            Configuração Incompleta
          </h3>
          <p className="text-red-600">
            Para usar o sistema, configure as credenciais FTP e WhatsApp nas configurações.
          </p>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
